import { foldForSearch } from "@/features/search/lib/highlight-match"
import { ACTION_VERBS, NAV_STOPWORDS } from "@/features/search/lib/nav-keywords"

/** How well a page or action matched: lower is better. */
export enum NavMatchRank {
  /** The label starts with the query, or every query word starts a label word. */
  Label = 0,
  /** At least one word was found only through a keyword. */
  Keyword = 1,
}

const ACTION_VERB_SET = new Set<string>(ACTION_VERBS)

/** The query's meaningful words: folded, split on spaces and apostrophes ("d'offrande"), stopwords dropped. */
export function navQueryWords(query: string): string[] {
  return foldForSearch(query)
    .split(/[\s'’]+/)
    .filter((word) => word.length > 0 && !NAV_STOPWORDS.has(word))
}

/** True when the query opens with an action verb ("créer un membre", "add visitor"). */
export function startsWithActionVerb(query: string): boolean {
  const first = navQueryWords(query)[0]
  return first !== undefined && ACTION_VERB_SET.has(first)
}

function tokens(values: readonly string[]): string[] {
  return values.flatMap((value) => foldForSearch(value).split(/[\s'’,/·-]+/)).filter(Boolean)
}

/**
 * Matches a page or action: every meaningful query word must START a word of the label or of a keyword
 * (so "membr" finds "Membres"), accents and case ignored. Returns null when it does not match.
 */
export function matchNavEntry(query: string, label: string, keywords: readonly string[]): NavMatchRank | null {
  const words = navQueryWords(query)
  if (words.length === 0) return null
  const labelTokens = tokens([label])
  const keywordTokens = tokens(keywords)

  let keywordOnly = false
  for (const word of words) {
    if (labelTokens.some((token) => token.startsWith(word))) continue
    if (keywordTokens.some((token) => token.startsWith(word))) {
      keywordOnly = true
      continue
    }
    return null
  }
  if (!keywordOnly || foldForSearch(label).startsWith(words.join(" "))) return NavMatchRank.Label
  return NavMatchRank.Keyword
}

/** The entries that match, best first (label matches before keyword matches, original order kept within a rank). */
export function rankNavEntries<T extends { title: string; keywords: readonly string[] }>(query: string, entries: readonly T[]): T[] {
  return entries
    .map((entry, index) => ({ entry, index, rank: matchNavEntry(query, entry.title, entry.keywords) }))
    .filter((item): item is { entry: T; index: number; rank: NavMatchRank } => item.rank !== null)
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .map((item) => item.entry)
}
